import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonialsData = [{
  author: "Robert M.",
  quote: "The compact loader we rented was in peak condition. Lynx Rentals’s reliability is the backbone of our site operations.",
  position: "Site Superintendent",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/avatar_robert_m.png"
}, {
  author: "Sarah L.",
  quote: "Fast availability and well-maintained gear. They understand that on a construction site, downtime is not an option.",
  position: "Project Manager",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/avatar_sarah_l.png"
}, {
  author: "David K.",
  quote: "Flexible rental terms and top-tier backhoes. Lynx Rentals is our first call for every new infrastructure project.",
  position: "General Contractor",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/avatar_david_k.png"
}, {
  author: "Elena G.",
  quote: "Professional service and high-performance machinery. Their fleet consistency helps us maintain strict project timelines.",
  position: "Operations Director",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/avatar_elena_g.png"
}];
export default function WordsFromTheRoad() {
  const [api, setApi] = useState<CarouselApi>();
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    if (!api) return;
    const intervalId = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 5000);
    return () => clearInterval(intervalId);
  }, [api]);
  return <section data-section-id="8763" id="field-success-stories" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615860/img/service_area_map_bg.png)] bg-cover bg-center opacity-5 grayscale mix-blend-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
        <div className="absolute inset-0 bg-radial-[circle_at_center,_var(--color-primary)_0%,_transparent_70%] opacity-5" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className="mb-16 text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="outline" className="border-primary/20 text-primary bg-primary/5 mb-2 rounded-sm font-mono uppercase tracking-widest text-xs">
            Operational Impact
          </Badge>
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter uppercase text-secondary">
            Field Success <span className="text-primary">Stories</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-medium">
            See how our fleet powers the most demanding job sites and keeps professional contractors ahead of schedule.
          </p>
        </div>

        {}
        <div className="w-full max-w-6xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 pb-4">
              {testimonialsData.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3 h-full">
                  <div className="h-full pt-2">
                    <Card className="h-full bg-secondary backdrop-blur-md border-border/10 hover:border-primary/60 transition-all duration-300 hover:shadow-xl group rounded-sm shadow-md">
                      <CardContent className="p-6 flex flex-col h-full gap-6">
                        {}
                        <div className="flex justify-between items-start">
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className="size-4 fill-primary text-primary" />)}
                          </div>
                          <Badge variant="secondary" className="text-[10px] px-2 h-5 bg-primary/20 text-white border border-primary/30 font-bold rounded-sm uppercase tracking-tighter">
                            Verified Rental
                          </Badge>
                        </div>

                        {}
                        <div className="flex-1 relative">
                          <Quote className="absolute -top-2 -left-2 size-12 text-primary/10 -scale-x-100" />
                          <blockquote className="relative z-10 text-white italic leading-relaxed pl-4 border-l-2 border-primary font-medium text-lg">
                            "{testimonial.quote}"
                          </blockquote>
                        </div>

                        {}
                        <div className="flex items-center gap-4 mt-auto pt-6 border-t border-white/10">
                          <SafeHydrate><Avatar className="size-12 border-2 border-primary/20 ring-2 ring-primary/10 group-hover:ring-primary/40 transition-all">
                            <AvatarImage src={testimonial.img} alt={testimonial.author} />
                            <AvatarFallback className="bg-primary/20 text-primary font-black uppercase">
                              {testimonial.author.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <div className="font-bold text-white uppercase tracking-tight">
                              {testimonial.author}
                            </div>
                            <div className="text-xs text-primary font-bold uppercase tracking-widest font-mono">
                              {testimonial.position}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="flex justify-center gap-4 mt-8 md:hidden">
               {}
            </div>
            
            <CarouselPrevious className="hidden md:flex -left-12 lg:-left-16 bg-white border-border/50 text-secondary hover:bg-primary hover:text-white hover:border-primary transition-all rounded-sm shadow-sm" />
            <CarouselNext className="hidden md:flex -right-12 lg:-right-16 bg-white border-border/50 text-secondary hover:bg-primary hover:text-white hover:border-primary transition-all rounded-sm shadow-sm" />
          </Carousel>
        </div>
      </div>
    </section>;
}